import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import { trackSimulatorClick, trackPhoneClick } from "../utils/analytics";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Phone, CheckCircle, AlertTriangle, Clock, Heart } from "lucide-react";
import BoutonRappel from "../components/BoutonRappel";
import GuidesLies from "../components/GuidesLies";

/**
 * Page destinée aux aidants — enfants, conjoints, proches qui cherchent pour
 * quelqu'un d'autre. Intention de recherche distincte de celle du bénéficiaire
 * lui-même : « aider mes parents à adapter leur maison », « ma mère est tombée,
 * que faire », « qui contacter pour aménager le logement de mon père ».
 * Aucune autre page du site ne s'adressait à eux, alors qu'ils sont à
 * l'origine d'une grande part des premiers appels.
 */

const FAQ = [
  {
    question: "Puis-je faire la demande MaPrimeAdapt' à la place de mon parent ?",
    answer:
      "Vous pouvez tout préparer avec lui, mais la demande est faite au nom du propriétaire occupant : c'est son dossier, ses ressources et sa signature. En pratique, vous pouvez être présent à la visite, rassembler les pièces, suivre les échanges et être notre interlocuteur au quotidien. Si votre parent est sous mesure de protection juridique, c'est la personne chargée de la mesure qui signe.",
  },
  {
    question: "Mes revenus à moi entrent-ils dans le calcul ?",
    answer:
      "Non. Les plafonds de ressources s'apprécient sur le foyer fiscal du propriétaire occupant, c'est-à-dire votre parent — et son conjoint s'il vit avec lui. Vos propres revenus d'enfant aidant n'entrent pas dans le calcul, même si vous participez au financement des travaux.",
  },
  {
    question: "Mon parent est locataire, que peut-on faire ?",
    answer:
      "MaPrimeAdapt' s'adresse aux propriétaires occupants et, sous conditions, aux propriétaires bailleurs. Si votre parent loue dans le parc privé, la demande passe par le propriétaire du logement : c'est lui qui perçoit l'aide et porte les travaux. S'il est locataire d'un bailleur social, il faut saisir le bailleur, qui a ses propres programmes d'adaptation. Dans les deux cas, le volet aides techniques de l'APA et les aides de la caisse de retraite restent mobilisables directement.",
  },
  {
    question: "Combien de temps faut-il compter entre le premier appel et la fin des travaux ?",
    answer:
      "Il faut prévoir plusieurs mois. La visite d'évaluation à domicile intervient sous une à deux semaines, les devis demandent deux à trois semaines, puis l'instruction du dossier par l'ANAH prend en général deux à quatre mois. Les travaux eux-mêmes durent de quelques jours à quelques semaines. Le point important : les travaux ne doivent pas commencer avant l'accord, sous peine de perdre l'aide.",
  },
  {
    question: "Mon parent refuse d'en parler, comment aborder le sujet ?",
    answer:
      "C'est la difficulté la plus fréquente, et elle est légitime : l'adaptation du logement est souvent vécue comme l'aveu d'un déclin. Ce qui fonctionne le mieux, d'expérience, est de partir d'un problème concret et limité — « la baignoire est difficile à enjamber » — plutôt que d'un projet global. La visite d'évaluation aide beaucoup : elle est gratuite, sans engagement, et le fait qu'un tiers pose le diagnostic déplace la conversation hors du rapport parent-enfant.",
  },
  {
    question: "Vivant loin, puis-je suivre le dossier à distance ?",
    answer:
      "Oui. Nous travaillons régulièrement avec des enfants qui habitent une autre région. Vous pouvez être en copie de tous les échanges, participer à la visite en visioconférence et valider les devis à distance, à condition que votre parent nous y autorise. C'est une demande courante et elle ne pose pas de difficulté.",
  },
];

const Aidants = () => {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Aider un parent âgé à adapter son logement | Guide aidants"
        description="Vous cherchez pour votre père ou votre mère : qui fait la demande, quels revenus comptent, quels délais prévoir. Le guide de l'aidant en Île-de-France."
        chemin="/aidants"
        donnees={[
          serviceJsonLd({
            nom: "Accompagnement des aidants dans l'adaptation du logement d'un proche",
            description:
              "Assistance à maîtrise d'ouvrage agréée ANAH pour les familles qui organisent l'adaptation du logement d'un parent âgé ou en perte d'autonomie en Île-de-France.",
            url: "/aidants",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Aidants", url: "/aidants" },
          ]),
          faqJsonLd(FAQ),
        ]}
      />

      <Header />

      <main id="contenu">

      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="text-orange-700" size={28} />
              <span className="font-semibold text-gray-700">Vous cherchez pour quelqu'un d'autre</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Aider un parent à adapter son logement
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              La plupart des gens qui nous appellent ne cherchent pas pour eux. Ce sont
              des enfants qui viennent de voir leur mère se rattraper au lavabo, un
              conjoint qui n'arrive plus à aider à la sortie de bain, une fille qui habite
              à 400 km et ne dort plus très bien. Cette page répond aux questions qui
              reviennent le plus souvent dans ces appels-là.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/simulateur"
                onClick={() => trackSimulatorClick("aidants_hero")}
                className="inline-block bg-orange-700 text-white text-lg font-bold px-8 py-4 rounded-lg hover:bg-orange-800 transition-colors text-center"
              >
                Vérifier l'éligibilité de mon parent
              </Link>
              <a
                href="tel:0939035444"
                onClick={() => trackPhoneClick("aidants_hero")}
                className="inline-flex items-center justify-center gap-2 border-2 border-orange-600 text-orange-700 text-lg font-bold px-8 py-4 rounded-lg hover:bg-orange-50 transition-colors"
              >
                <Phone size={20} />
                09 39 03 54 44
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Le déclencheur */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Ce qui déclenche l'appel, en général</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Il y a presque toujours un événement précis. Une chute sans gravité, mais qui
              a fait peur. Une hospitalisation dont le retour à domicile pose question. Un
              médecin ou une infirmière qui dit, en passant, que la salle de bain n'est plus
              adaptée. Parfois simplement le constat qu'un parent ne monte plus à l'étage et
              dort dans le salon depuis des mois.
            </p>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={22} />
                <p className="text-gray-800 leading-relaxed">
                  L'erreur la plus coûteuse, et de loin : faire réaliser les travaux avant
                  d'avoir l'accord de l'ANAH. Un devis signé et des travaux engagés avant la
                  notification font perdre le droit à l'aide, sans rattrapage possible.
                  Quand l'urgence est réelle, il existe d'autres leviers plus rapides — nous
                  vous le disons dès le premier échange.
                </p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">
              L'autre réflexe utile est de ne pas tout traiter d'un coup. Un dossier qui
              commence par la salle de bain et les circulations règle l'essentiel du risque
              de chute. Le reste peut venir plus tard.
            </p>
          </div>
        </div>
      </section>

      {/* Votre rôle exact */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Votre rôle exact dans le dossier</h2>
            <div className="space-y-6">
              {[
                {
                  titre: "Le dossier reste au nom de votre parent",
                  texte:
                    "C'est lui le propriétaire occupant, donc le demandeur. Ses ressources déterminent le taux d'aide, et c'est sa signature qui engage. Vos propres revenus n'entrent jamais dans le calcul.",
                },
                {
                  titre: "Vous pouvez être l'interlocuteur au quotidien",
                  texte:
                    "Avec son accord, nous échangeons avec vous : prise de rendez-vous, pièces à réunir, lecture des devis, suivi de l'instruction. C'est le cas de figure le plus fréquent, et il ne pose aucune difficulté administrative.",
                },
                {
                  titre: "Vous pouvez participer au financement",
                  texte:
                    "Rien n'interdit à un enfant de financer le reste à charge. Cela n'a aucun effet sur le calcul de l'aide. Pensez simplement à conserver les justificatifs : selon les montants, cela peut avoir des conséquences successorales.",
                },
                {
                  titre: "S'il y a une mesure de protection",
                  texte:
                    "Tutelle, curatelle, habilitation familiale : c'est la personne chargée de la mesure qui signe, et certains actes demandent l'autorisation du juge. Signalez-le dès le départ, cela change le calendrier.",
                },
              ].map((b) => (
                <div key={b.titre} className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{b.titre}</h3>
                  <p className="text-gray-700 leading-relaxed">{b.texte}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Aborder le sujet */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Comment en parler sans braquer</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              C'est la partie dont personne ne parle et qui bloque le plus de projets. Un
              parent qui refuse une barre d'appui ne refuse pas la barre : il refuse ce
              qu'elle dit de lui. Trois choses fonctionnent, d'expérience.
            </p>
            <ul className="space-y-4 mb-6">
              {[
                "Partir d'un problème précis plutôt que d'un projet global. « La baignoire est haute » s'entend mieux que « il faut adapter la maison ».",
                "Mettre en avant le confort avant la sécurité. Une douche de plain-pied est plus agréable, c'est vrai, et c'est un argument recevable.",
                "Faire poser le diagnostic par un tiers. La visite d'évaluation est gratuite et sans engagement ; le fait qu'elle vienne de l'extérieur sort la discussion du rapport parent-enfant.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle className="text-green-700 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Et s'il refuse malgré tout, la visite reste utile : vous saurez ce qui est
              possible, à quel prix et sous quel délai, le jour où la situation changera.
            </p>
          </div>
        </div>
      </section>

      {/* Calendrier */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-8">
              <Clock className="text-orange-700" size={28} />
              <h2 className="text-3xl font-bold">Le calendrier réel, étape par étape</h2>
            </div>
            <div className="space-y-4">
              {[
                ["Premier échange téléphonique", "le jour même", "On vérifie l'éligibilité de principe et on répond aux questions de financement."],
                ["Visite d'évaluation à domicile", "sous 1 à 2 semaines", "Gratuite. On relève les contraintes du logement et on établit les préconisations."],
                ["Devis d'entreprises", "2 à 3 semaines", "Nous consultons des artisans du secteur et vous présentons les devis comparés."],
                ["Dépôt et instruction du dossier", "2 à 4 mois", "L'ANAH instruit. Aucun travail ne doit commencer pendant cette période."],
                ["Travaux", "de quelques jours à quelques semaines", "Selon l'ampleur. Nous suivons le chantier et la conformité aux devis."],
                ["Versement de l'aide", "après réception", "L'aide est versée sur présentation des factures, une fois les travaux réceptionnés."],
              ].map(([etape, delai, texte], i) => (
                <div key={etape} className="bg-white p-6 rounded-lg border border-gray-200 flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-700 text-white font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {etape} <span className="font-normal text-orange-700">— {delai}</span>
                    </h3>
                    <p className="text-gray-700 mt-1 leading-relaxed">{texte}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">Les questions que posent les aidants</h2>
            <div className="space-y-6">
              {FAQ.map((q) => (
                <div key={q.question} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{q.question}</h3>
                  <p className="text-gray-700 leading-relaxed">{q.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold mb-4">Pour aller plus loin</h2>
            <div className="flex flex-wrap gap-3">
              {[
                ["/maprimeadapt", "Tout sur MaPrimeAdapt'"],
                ["/salle-de-bain", "Douche sénior : prix et aides"],
                ["/monte-escalier", "Monte-escalier : prix et aides"],
                ["/cumul-aides-ma-prime-adapt-idf", "Cumuler les aides en Île-de-France"],
                ["/blog", "Nos guides pratiques"],
              ].map(([to, label]) => (
                <Link
                  key={to}
                  to={to}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:border-orange-500 hover:text-orange-700 transition-colors"
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-orange-600 to-orange-700 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Parlons de la situation de votre proche
          </h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
            Un échange de quinze minutes suffit à savoir si le dossier tient, et par quoi
            commencer. Sans engagement.
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <BoutonRappel
              variant="default"
              avecIcone={false}
              className="bg-white text-orange-700 hover:bg-gray-100 text-xl font-bold px-10 py-4 h-auto rounded-lg shadow-lg"
            />
            <a
              href="tel:0939035444"
              onClick={() => trackPhoneClick("aidants_cta")}
              className="bg-transparent border-2 border-white text-white text-xl font-bold px-10 py-4 rounded-lg hover:bg-white hover:text-orange-700 transition-colors flex items-center gap-2"
            >
              <Phone size={24} />
              09 39 03 54 44
            </a>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["gir-evaluation-autonomie", "comment-obtenir-maprimeadapt"]} />

      </main>

      <Footer />
    </div>
  );
};

export default Aidants;
